// src/hooks/useEntries.js - Hook for accessing customer activity entries
import useEntryStore from '../stores/EntryStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing customer activity entries from EntryStore
 *
 * Uses Zustand EntryStore for state management.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Entry data and operations
 */
export const useEntries = () => {
  const socket = useSocket();

  // Entries by customer
  const entriesByCustomer = useEntryStore(state => state.entriesByCustomer);

  // Entry types (for filtering UI)
  const entryTypes = useEntryStore(state => state.entryTypes);
  const entryTypesLoading = useEntryStore(state => state.entryTypesLoading);
  const entryTypesLoaded = useEntryStore(state => state.entryTypesLoaded);

  // Operations
  const loadCustomerEntries = useEntryStore(state => state.loadCustomerEntries);
  const loadEntryTypes = useEntryStore(state => state.loadEntryTypes);

  // Utility functions
  const getEntriesForCustomer = useEntryStore(state => state.getEntriesForCustomer);
  const isLoadingEntriesForCustomer = useEntryStore(state => state.isLoadingEntriesForCustomer);
  const getTotalEntriesForCustomer = useEntryStore(state => state.getTotalEntriesForCustomer);
  const getFilterForCustomer = useEntryStore(state => state.getFilterForCustomer);
  const clearEntriesForCustomer = useEntryStore(state => state.clearEntriesForCustomer);

  // Connection state
  const isConnected = useEntryStore(state => state.isConnected);

  // Internal initialization state
  const _initialized = useEntryStore(state => state._initialized);

  // Fallback initialization check
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('useEntries: Store not initialized at app level, trying fallback');
    socket.initializeStore('EntryStore');
  }

  return {
    // Data
    entriesByCustomer,
    entryTypes,
    entryTypesLoading,
    entryTypesLoaded,

    // Operations
    loadCustomerEntries,
    loadEntryTypes,

    // Utility functions
    getEntriesForCustomer,
    isLoadingEntriesForCustomer,
    getTotalEntriesForCustomer,
    getFilterForCustomer,
    clearEntriesForCustomer,

    // Connection state
    isConnected: socket.isAuthenticated
  };
};

export default useEntries;
